class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();
		//this.addCloseButtonHandlers();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");
		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			this.open(cartLink);
		});
		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	addCloseButtonHandlers() {
		const detailsElement = this.querySelector('#Details-CartDrawer');
		const closeButton = this.querySelector('#cart_note-close');
		const applyButton = this.querySelector('#apply_button');

		if (detailsElement && closeButton && applyButton) {
			function closeDetails(event) {
				detailsElement.removeAttribute('open');
			}

			closeButton.addEventListener('click', closeDetails);
			applyButton.addEventListener('click', closeDetails);
		}
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
			this.addCloseButtonHandlers();
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("overflow-hidden");
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("overflow-hidden");
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.open();
		});
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

class CartDiscount extends HTMLElement {
	connectedCallback() {
		this.input = this.querySelector(".cart-discount__input");
		this.applyButton = this.querySelector(".cart-discount__apply");
		this.error = this.querySelector(".cart-discount__error");
		this.applyButton.addEventListener("click", this.apply.bind(this));
		this.input.addEventListener("keydown", (event) => {
			if (event.key === "Enter") {
				event.preventDefault();
				this.apply();
			}
		});
	}

	async apply() {
		const code = this.input.value.trim();
		if (!code || this.applyButton.classList.contains("disabled")) return;
		this.error.hidden = true;
		this.applyButton.classList.add("disabled");
		try {
			const previousCart = await fetch(`${routes.cart_url}.js`).then((r) => r.json());
			const previousCodes = (previousCart.discount_codes || [])
				.filter((d) => d.applicable)
				.map((d) => d.code);
			const codes = previousCodes.concat(code).join(",");
			const cart = await fetch(routes.cart_update_url, {
				...fetchConfig(),
				body: JSON.stringify({ discount: codes }),
			}).then((r) => r.json());
			const applied = (cart.discount_codes || []).find(
				(d) => d.code.toLowerCase() === code.toLowerCase()
			);
			if (!applied || !applied.applicable) {
				// roll back to the previously applied codes so the rejected one doesn't linger
				await fetch(routes.cart_update_url, {
					...fetchConfig(),
					body: JSON.stringify({ discount: previousCodes.join(",") }),
				});
				this.error.hidden = false;
				return;
			}
			await this.renderCart();
		} catch (e) {
			this.error.hidden = false;
		} finally {
			this.applyButton.classList.remove("disabled");
		}
	}

	async renderCart() {
		const response = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`);
		const sections = await response.json();
		const parse = (html, selector) =>
			new DOMParser().parseFromString(html, "text/html").querySelector(selector);
		const drawerInner = document.querySelector("cart-drawer .drawer__inner");
		const newInner = parse(sections["cart-drawer"], ".drawer__inner");
		if (drawerInner && newInner) drawerInner.innerHTML = newInner.innerHTML;
		const bubble = document.getElementById("cart-icon-bubble");
		const newBubble = parse(sections["cart-icon-bubble"], ".shopify-section");
		if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;
	}
}

customElements.define("cart-discount", CartDiscount);
